import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Lock, User, Upload, Globe, ShieldAlert, ShieldCheck, Trash2 } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Settings() {
  const { user, checkAuth } = useAuth();
  const [profileForm, setProfileForm] = useState({ name: '', surname: '', contact_phone: '', contact_email: '', preferred_language: 'it' });
  const [pwdForm, setPwdForm] = useState({ current_password: '', new_password: '' });
  const [profileMsg, setProfileMsg] = useState('');
  const [pwdMsg, setPwdMsg] = useState('');
  const [verifyMsg, setVerifyMsg] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deletePwd, setDeletePwd] = useState('');
  const [deleteMsg, setDeleteMsg] = useState('');

  useEffect(() => {
    if (user) {
      setProfileForm({
        name: user.name || '',
        surname: user.surname || '',
        contact_phone: user.contact_phone || '',
        contact_email: user.email || '',
        preferred_language: user.preferred_language || 'it',
      });
    }
  }, [user]);

  const saveProfile = async () => {
    if ((profileForm.name || '').includes('@') || (profileForm.surname || '').includes('@')) {
      setProfileMsg('Il nome non può contenere un indirizzo email');
      return;
    }
    try {
      await axios.put(`${API}/auth/profile`, profileForm, { withCredentials: true });
      setProfileMsg('Profilo aggiornato!');
      checkAuth();
      setTimeout(() => setProfileMsg(''), 3000);
    } catch (e) { setProfileMsg(e.response?.data?.detail || 'Errore nel salvataggio'); }
  };

  const changePassword = async () => {
    try {
      await axios.put(`${API}/auth/password`, pwdForm, { withCredentials: true });
      setPwdMsg('Password aggiornata!');
      setPwdForm({ current_password: '', new_password: '' });
      setTimeout(() => setPwdMsg(''), 3000);
    } catch (e) {
      setPwdMsg(e.response?.data?.detail || 'Errore');
    }
  };

  const uploadProfileImage = async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    try {
      await axios.post(`${API}/auth/profile-image`, fd, { withCredentials: true });
      setProfileMsg('Immagine aggiornata!');
      checkAuth();
      setTimeout(() => setProfileMsg(''), 3000);
    } catch { setProfileMsg('Errore upload'); }
  };

  const sendVerification = async () => {
    setVerifyMsg('');
    try {
      const { data } = await axios.post(`${API}/auth/send-verification`, {}, { withCredentials: true });
      if (data.status === 'already_verified') {
        setVerifyMsg('Email già verificata.');
      } else {
        setVerifyMsg('Email di verifica inviata! Controlla la tua casella.');
      }
      setTimeout(() => setVerifyMsg(''), 6000);
    } catch (e) {
      setVerifyMsg(e.response?.data?.detail || 'Errore invio');
    }
  };

  const deleteAccount = async () => {
    setDeleteMsg('');
    if (deleteConfirm.trim().toUpperCase() !== 'ELIMINA') {
      setDeleteMsg('Digita ELIMINA per confermare');
      return;
    }
    try {
      await axios.post(`${API}/auth/delete-account`, { password: deletePwd }, { withCredentials: true });
      window.location.href = '/?account_deleted=1';
    } catch (e) {
      setDeleteMsg(e.response?.data?.detail || 'Errore durante l\'eliminazione');
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[#09090B] pt-20 flex items-center justify-center">
        <div className="text-center">
          <p className="text-zinc-400 mb-4">Accedi per visualizzare le impostazioni</p>
          <Link to="/login" className="text-[#C5A059] hover:underline">Accedi</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090B] pt-20" data-testid="settings-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-10">
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#C5A059] mb-2">Account</p>
          <h1 className="text-3xl md:text-4xl tracking-tight font-medium text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Impostazioni
          </h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="settings-section">
          {/* Profile */}
          <div className="bg-zinc-900 border border-zinc-800 p-6 space-y-4">
            <h3 className="text-sm font-medium text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Profilo</h3>
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-zinc-800 border border-zinc-700 flex items-center justify-center overflow-hidden shrink-0">
                {user.picture ? <img src={user.picture} alt="" className="w-full h-full object-cover" /> : <User size={24} className="text-zinc-500" />}
              </div>
              <div>
                <input type="file" accept="image/*" onChange={e => e.target.files[0] && uploadProfileImage(e.target.files[0])} className="hidden" id="profile-img-upload" />
                <label htmlFor="profile-img-upload" className="flex items-center gap-2 px-3 py-1.5 border border-zinc-700 text-xs text-zinc-300 hover:border-zinc-500 cursor-pointer transition-colors" data-testid="upload-profile-img">
                  <Upload size={12} /> Cambia Immagine
                </label>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-zinc-400 text-[10px] mb-1 block">Nome</Label>
                <Input value={profileForm.name} onChange={e => setProfileForm(p => ({...p, name: e.target.value}))} className="bg-zinc-800 border-zinc-700 text-white h-9 text-xs" data-testid="settings-name" />
              </div>
              <div>
                <Label className="text-zinc-400 text-[10px] mb-1 block">Cognome</Label>
                <Input value={profileForm.surname} onChange={e => setProfileForm(p => ({...p, surname: e.target.value}))} className="bg-zinc-800 border-zinc-700 text-white h-9 text-xs" data-testid="settings-surname" />
              </div>
            </div>
            <div>
              <Label className="text-zinc-400 text-[10px] mb-1 block">Telefono <span className="text-zinc-600">(non visibile)</span></Label>
              <Input value={profileForm.contact_phone} onChange={e => setProfileForm(p => ({...p, contact_phone: e.target.value}))} className="bg-zinc-800 border-zinc-700 text-white h-9 text-xs" data-testid="settings-phone" />
            </div>
            <div>
              <Label className="text-zinc-400 text-[10px] mb-1 block">Email contatto <span className="text-zinc-600">(non visibile)</span></Label>
              <Input value={profileForm.contact_email} onChange={e => setProfileForm(p => ({...p, contact_email: e.target.value}))} className="bg-zinc-800 border-zinc-700 text-white h-9 text-xs" data-testid="settings-email" />
            </div>
            <div>
              <Label className="text-zinc-400 text-[10px] mb-1 block flex items-center gap-1.5"><Globe size={11} /> Lingua preferita <span className="text-zinc-600">(email e notifiche)</span></Label>
              <div className="relative">
                <select
                  value={profileForm.preferred_language}
                  onChange={e => setProfileForm(p => ({...p, preferred_language: e.target.value}))}
                  className="w-full bg-zinc-800 border border-zinc-700 text-white h-9 text-xs px-3 pr-8 appearance-none cursor-pointer focus:outline-none focus:border-[#C5A059]"
                  data-testid="settings-lang"
                >
                  <option value="it">Italiano</option>
                  <option value="en">English</option>
                </select>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none text-xs">▾</div>
              </div>
            </div>
            {profileMsg && <p className={`text-xs ${profileMsg.includes('Errore') ? 'text-red-400' : 'text-emerald-400'}`}>{profileMsg}</p>}
            <button onClick={saveProfile} className="bg-[#C5A059] text-black px-5 py-2 text-xs font-medium hover:bg-[#D4B271] transition-colors" data-testid="save-profile-btn">
              Salva Profilo
            </button>
          </div>

          {/* Password */}
          <div className="bg-zinc-900 border border-zinc-800 p-6 space-y-4">
            <h3 className="text-sm font-medium text-white flex items-center gap-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              <Lock size={14} /> Cambia Password
            </h3>
            <div>
              <Label className="text-zinc-400 text-[10px] mb-1 block">Password Attuale</Label>
              <Input type="password" value={pwdForm.current_password} onChange={e => setPwdForm(p => ({...p, current_password: e.target.value}))} className="bg-zinc-800 border-zinc-700 text-white h-9 text-xs" data-testid="settings-current-pwd" />
            </div>
            <div>
              <Label className="text-zinc-400 text-[10px] mb-1 block">Nuova Password</Label>
              <Input type="password" value={pwdForm.new_password} onChange={e => setPwdForm(p => ({...p, new_password: e.target.value}))} className="bg-zinc-800 border-zinc-700 text-white h-9 text-xs" data-testid="settings-new-pwd" />
            </div>
            {pwdMsg && <p className={`text-xs ${pwdMsg.includes('Errore') || pwdMsg.includes('non corretta') ? 'text-red-400' : 'text-emerald-400'}`}>{pwdMsg}</p>}
            <button onClick={changePassword} className="border border-zinc-700 text-zinc-300 px-5 py-2 text-xs hover:border-zinc-500 transition-colors" data-testid="change-pwd-btn">
              Aggiorna Password
            </button>
          </div>
        </div>

        {/* Email verification — shown only if not verified */}
        {user && user.email_verified === false && (
          <div className="mt-6 bg-zinc-900 border border-[#C5A059]/40 p-6 space-y-3" data-testid="email-verification-section">
            <h3 className="text-sm font-medium text-white flex items-center gap-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              <ShieldAlert size={14} className="text-[#C5A059]" /> Verifica Email
            </h3>
            <p className="text-xs text-zinc-400 leading-relaxed">
              Il tuo indirizzo email <span className="text-zinc-200">{user.email}</span> non è ancora stato verificato. La verifica migliora la sicurezza del tuo account e ci permette di contattarti in modo affidabile.
            </p>
            {verifyMsg && <p className={`text-xs ${verifyMsg.includes('Errore') ? 'text-red-400' : 'text-emerald-400'}`}>{verifyMsg}</p>}
            <button onClick={sendVerification} className="bg-[#C5A059] text-black px-5 py-2 text-xs font-medium hover:bg-[#D4B271] transition-colors" data-testid="send-verification-btn">
              Invia email di verifica
            </button>
          </div>
        )}
        {user && user.email_verified === true && (
          <div className="mt-6 bg-zinc-900 border border-zinc-800 px-6 py-4 flex items-center gap-3" data-testid="email-verified-badge">
            <ShieldCheck size={16} className="text-emerald-400" />
            <p className="text-xs text-zinc-400">Email <span className="text-zinc-200">{user.email}</span> verificata ✓</p>
          </div>
        )}

        {/* Danger zone — delete account */}
        <div className="mt-10 border border-red-500/30 bg-red-500/[0.03] p-6 space-y-4" data-testid="danger-zone">
          <div>
            <h3 className="text-sm font-medium text-red-400 flex items-center gap-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              <Trash2 size={14} /> Elimina Account
            </h3>
            <p className="text-xs text-zinc-500 mt-1 leading-relaxed">
              L'account verrà disattivato immediatamente. I tuoi dati saranno eliminati definitivamente dopo {30} giorni; entro questo periodo puoi contattarci per ripristinarli. I tuoi annunci pubblicati verranno trasferiti allo staff AeroDeals e resi "da rivendicare".
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-zinc-400 text-[10px] mb-1 block">Password (per conferma)</Label>
              <Input type="password" value={deletePwd} onChange={e => setDeletePwd(e.target.value)} placeholder="La tua password" className="bg-zinc-900 border-zinc-700 text-white h-9 text-xs" data-testid="delete-pwd" />
            </div>
            <div>
              <Label className="text-zinc-400 text-[10px] mb-1 block">Digita <span className="text-red-400 font-mono">ELIMINA</span> per confermare</Label>
              <Input type="text" value={deleteConfirm} onChange={e => setDeleteConfirm(e.target.value)} placeholder="ELIMINA" className="bg-zinc-900 border-zinc-700 text-white h-9 text-xs font-mono" data-testid="delete-confirm" />
            </div>
          </div>
          {deleteMsg && <p className="text-xs text-red-400">{deleteMsg}</p>}
          <button onClick={deleteAccount} className="bg-red-500/90 text-white px-5 py-2 text-xs font-medium hover:bg-red-500 transition-colors" data-testid="delete-account-btn">
            Elimina definitivamente
          </button>
        </div>
      </div>
    </div>
  );
}
